import { Users, MessageSquare, Calendar, Clock } from 'lucide-react';
import StatsCard from '../components/StatsCard';
import StatusBadge from '../components/StatusBadge';
import { useDashboardStats } from '../hooks/useApi';
import { formatRelativeTime } from '../lib/utils';

export default function DashboardPage() {
  const { data: stats, isLoading } = useDashboardStats();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const totalApplicants = stats?.totals?.totalApplicants || 0;
  const scheduledCount = stats?.totals?.scheduledCount || 0;

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Stats Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Dashboard</h2>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
        <StatsCard
          title="Total Applicants"
          value={totalApplicants}
          description="From Indeed"
          icon={<Users className="w-6 h-6 text-primary-600" />}
        />
        <StatsCard
          title="Today"
          value={stats?.totals?.newCount || 0}
          icon={<Clock className="w-6 h-6 text-primary-600" />}
        />
        <StatsCard
          title="Unread"
          value={stats?.totals?.unreadMessages || 0}
          icon={<MessageSquare className="w-6 h-6 text-primary-600" />}
        />
        <StatsCard
          title="Interviews"
          value={scheduledCount}
          icon={<Calendar className="w-6 h-6 text-primary-600" />}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Status Breakdown */}
        <div className="card p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">Applicants by Status</h3>
          <div className="space-y-3">
            {stats?.totals?.newCount != null && (
              <>
                <div className="flex items-center justify-between">
                  <StatusBadge status="new" />
                  <span className="text-sm font-medium text-gray-900">{stats.totals.newCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <StatusBadge status="contacted" />
                  <span className="text-sm font-medium text-gray-900">{stats.totals.contactedCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <StatusBadge status="responded" />
                  <span className="text-sm font-medium text-gray-900">{stats.totals.respondedCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <StatusBadge status="scheduled" />
                  <span className="text-sm font-medium text-gray-900">{stats.totals.scheduledCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <StatusBadge status="hired" />
                  <span className="text-sm font-medium text-gray-900">{stats.totals.hiredCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <StatusBadge status="rejected" />
                  <span className="text-sm font-medium text-gray-900">{stats.totals.rejectedCount}</span>
                </div>
              </>
            )}
            {!stats?.totals && (
              <p className="text-sm text-gray-500">No applicants yet</p>
            )}
          </div>
        </div>

        {/* Today's Messages */}
        <div className="card p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">Today's Activity</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Inbound Messages</span>
              <span className="text-lg font-semibold text-gray-900">
                {stats?.totals?.inboundCount || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Outbound Messages</span>
              <span className="text-lg font-semibold text-gray-900">
                {stats?.totals?.outboundCount || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Recent Applicants */}
        <div className="card p-4 lg:p-6 lg:col-span-3">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">Recent Applicants</h3>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Job</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Applied</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats?.recentApplicants?.map((applicant) => (
                  <tr key={applicant.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {applicant.first_name} {applicant.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{applicant.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{applicant.job_title}</td>
                    <td className="py-3 px-4">
                      <StatusBadge status={applicant.status} />
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {formatRelativeTime(applicant.created_at)}
                    </td>
                  </tr>
                ))}
                {(!stats?.recentApplicants || stats.recentApplicants.length === 0) && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-sm text-gray-500">
                      No applicants yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="sm:hidden space-y-3">
            {stats?.recentApplicants?.map((applicant) => (
              <div key={applicant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">
                    {applicant.first_name} {applicant.last_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{applicant.job_title}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <StatusBadge status={applicant.status} />
                </div>
              </div>
            ))}
            {(!stats?.recentApplicants || stats.recentApplicants.length === 0) && (
              <p className="py-8 text-center text-sm text-gray-500">No applicants yet</p>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
